import { RefreshCcw, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAppVersionCheck } from '@/hooks/useAppVersionCheck';

/**
 * Site-wide banner that appears when the user's loaded JS bundle is
 * older than what the server is currently serving (i.e. a deploy
 * happened after their tab loaded). Replaces the "tell tenants to
 * hard-refresh" pattern with an in-product prompt the user can act on
 * when they're ready — not auto-reloading, since they may be
 * mid-edit.
 *
 * Pinned to the top of the viewport, above the route content. Sticky
 * across page navigations within the SPA. Operator can dismiss it
 * (×) — but the next interval check will surface it again until they
 * actually refresh. Refresh button reloads the page (and picks up the
 * new bundle).
 */
export function AppVersionBanner() {
  const { newVersionAvailable, refresh, triggerCheck } = useAppVersionCheck();
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  // Force a version check on every SPA navigation. Operators rarely
  // sit still on one route — clicking anywhere in the app re-polls
  // version.json so the banner surfaces within seconds of a deploy
  // rather than waiting for the 60s interval.
  useEffect(() => {
    triggerCheck();
  }, [location.pathname, triggerCheck]);

  if (!newVersionAvailable || dismissed) return null;

  return (
    <div
      role="status"
      className={cn(
        'sticky top-0 z-50 flex items-center gap-3 border-b border-primary/20 bg-primary/10 px-4 py-2 text-[12.5px]',
      )}
    >
      <Sparkles className="h-4 w-4 shrink-0 text-primary" />
      <div className="flex-1 leading-tight">
        <span className="font-semibold text-foreground">A new version of Zafroi is available.</span>{' '}
        <span className="text-muted-foreground">
          Refresh when you&apos;re ready — your work in progress will be lost on refresh.
        </span>
      </div>
      <Button size="sm" onClick={refresh} className="shrink-0">
        <RefreshCcw className="h-3.5 w-3.5" />
        Refresh now
      </Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="rounded-md p-1 text-muted-foreground hover:bg-primary/15 hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
